import * as React from "react"
import { MoveRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { toneClass } from "@/lib/status"
import { formatNumber } from "@/lib/format"
import { getErrorMessage } from "@/lib/errors"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { notify } from "@/components/ui/notify"

/*
  Board — kanban columns with counts and cards you can move: drag and drop (mouse), the keyboard (Space to
  pick up, ←/→ to choose a column, Space/Enter to drop, Esc to cancel) or the card's "Move to…" menu (touch,
  screen readers). For 09 #7 (Execution board), 14 #9 (Blocked board by reason), 12 #10 (team load).

    <Board
      ariaLabel="Execution tasks by status"
      columns={[
        { key: 'Not Started', label: 'Not started', tone: 'gray' },
        { key: 'In Progress', label: 'In progress', tone: 'blue' },
        { key: 'Bottleneck', label: 'Bottleneck', tone: 'red', hint: 'Needs a decision' },
        { key: 'Done', label: 'Done', tone: 'navy' },
      ]}
      items={tasks} getKey={(t) => t.id} getColumn={(t) => t.status} getCardLabel={(t) => t.name}
      renderCard={(t) => <TaskCard task={t} />}
      onMove={async (task, toColumn) => { await api.put(`/tasks/${task.id}`, { status: toColumn }); reload(); }}
      canMove={(task, toColumn) => toColumn !== 'Done' || task.progress === 100}
      onOpen={(t) => openTask(t)} />

  The board does not reorder `items` itself: `onMove` saves and the page passes the new items (optimistic or
  after reload). While `onMove` runs the card shows as busy; if it throws, the error is shown as a toast (or
  passed to `onMoveError`). Without `onMove` the board is read-only (no drag, no menu).
  Column: { key, label, tone (dot), hint (small text under the label), count (overrides the item count),
  footer (node), testId }. Props: columns, items, getKey, getColumn, renderCard(item, { busy, picked }),
  getCardLabel (announcements / menu; default the key), onMove(item, toColumnKey), canMove(item, toColumnKey),
  onMoveError, onOpen(item) (Enter on a card), columnLimit (50 cards, then "Show N more"), emptyText,
  columnWidth (272), ariaLabel, className, testId ('board').
*/
const INSTRUCTIONS = "Press Space to pick up the card, Left and Right arrows to choose a column, Space or Enter to drop it, Escape to cancel."

function Board({
  columns,
  items,
  getKey = (item) => item.id,
  getColumn,
  renderCard,
  getCardLabel,
  onMove,
  canMove,
  onMoveError,
  onOpen,
  columnLimit = 50,
  emptyText = "Nothing here",
  columnWidth = 272,
  ariaLabel = "Board",
  className,
  testId = "board",
}) {
  const instructionsId = React.useId()
  const [dragKey, setDragKey] = React.useState(null)
  const [overColumn, setOverColumn] = React.useState(null)
  const [picked, setPicked] = React.useState(null) // { key, from, target }
  const [busyKeys, setBusyKeys] = React.useState(() => new Set())
  const [expanded, setExpanded] = React.useState(() => new Set())
  const [announcement, setAnnouncement] = React.useState("")
  const cardRefs = React.useRef(new Map())
  const columnRefs = React.useRef(new Map())
  const movable = typeof onMove === "function"

  const label = React.useCallback((item) => String(getCardLabel ? getCardLabel(item) : getKey(item)), [getCardLabel, getKey])
  const columnLabel = (key) => columns.find((column) => column.key === key)?.label || String(key)
  const byKey = React.useMemo(() => new Map(items.map((item) => [String(getKey(item)), item])), [items, getKey])
  const grouped = React.useMemo(() => {
    const map = new Map(columns.map((column) => [column.key, []]))
    items.forEach((item) => {
      const key = getColumn(item)
      if (map.has(key)) map.get(key).push(item)
    })
    return map
  }, [columns, items, getColumn])

  const allowed = React.useCallback(
    (item, toKey) => movable && toKey !== getColumn(item) && (!canMove || canMove(item, toKey)),
    [movable, canMove, getColumn]
  )

  const move = async (item, toKey) => {
    if (!allowed(item, toKey)) return
    const key = String(getKey(item))
    setBusyKeys((current) => new Set(current).add(key))
    setAnnouncement(`Moving ${label(item)} to ${columnLabel(toKey)}…`)
    try {
      await onMove(item, toKey)
      setAnnouncement(`${label(item)} moved to ${columnLabel(toKey)}.`)
    } catch (error) {
      setAnnouncement(`Could not move ${label(item)}.`)
      if (onMoveError) onMoveError(error, item, toKey)
      else notify.error(`Could not move “${label(item)}”`, { description: getErrorMessage(error) })
    } finally {
      setBusyKeys((current) => {
        const next = new Set(current)
        next.delete(key)
        return next
      })
      // keep keyboard focus on the card wherever it is rendered now
      window.requestAnimationFrame(() => cardRefs.current.get(key)?.focus())
    }
  }

  const onCardKeyDown = (event, item) => {
    if (event.target !== event.currentTarget) return
    const key = String(getKey(item))
    const from = getColumn(item)
    if (picked && picked.key === key) {
      const index = columns.findIndex((column) => column.key === picked.target)
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        event.preventDefault()
        const step = event.key === "ArrowRight" ? 1 : -1
        let next = index
        for (let i = 0; i < columns.length; i += 1) {
          next = (next + step + columns.length) % columns.length
          if (columns[next].key === from || allowed(item, columns[next].key)) break
        }
        const target = columns[next].key
        setPicked({ ...picked, target })
        // keep the column the card would go to on screen (the board scrolls sideways)
        columnRefs.current.get(target)?.scrollIntoView?.({ block: "nearest", inline: "nearest", behavior: "smooth" })
        setAnnouncement(target === from ? `Back in ${columnLabel(target)}.` : `Over ${columnLabel(target)}.`)
      } else if (event.key === " " || event.key === "Enter") {
        event.preventDefault()
        setPicked(null)
        if (picked.target !== from) move(item, picked.target)
        else setAnnouncement(`${label(item)} dropped back in ${columnLabel(from)}.`)
      } else if (event.key === "Escape") {
        event.preventDefault()
        setPicked(null)
        setAnnouncement(`Move cancelled. ${label(item)} stays in ${columnLabel(from)}.`)
      }
      return
    }
    if (event.key === " " && movable) {
      event.preventDefault()
      setPicked({ key, from, target: from })
      setAnnouncement(`Picked up ${label(item)} in ${columnLabel(from)}. Use Left and Right to choose a column.`)
    } else if (event.key === "Enter" && onOpen) {
      event.preventDefault()
      onOpen(item)
    }
  }

  // A keyboard pick-up ends when focus leaves the card (Tab away, a click elsewhere): otherwise the card would
  // stay ringed with a stale "→ Done" hint and a later Space would drop it there.
  const onCardBlur = (event, item) => {
    const key = String(getKey(item))
    if (picked?.key !== key || event.currentTarget.contains(event.relatedTarget)) return
    setPicked(null)
    setAnnouncement(`Move cancelled. ${label(item)} stays in ${columnLabel(getColumn(item))}.`)
  }

  const onDrop = (event, columnKey) => {
    event.preventDefault()
    const key = event.dataTransfer?.getData("text/plain") || dragKey
    setOverColumn(null)
    setDragKey(null)
    const item = byKey.get(String(key))
    if (item) move(item, columnKey)
  }

  return (
    <div className={cn("relative w-full min-w-0 [contain:inline-size]", className)} data-testid={testId}>
      <p id={instructionsId} className="sr-only">{movable ? INSTRUCTIONS : ""}</p>
      <div aria-live="assertive" className="sr-only" data-testid={`${testId}-live`}>{announcement}</div>
      <div
        className="flex snap-x gap-3 overflow-x-auto pb-2"
        role="region"
        aria-label={ariaLabel}
      >
        {columns.map((column) => {
          const cards = grouped.get(column.key) || []
          const count = column.count ?? cards.length
          const limit = expanded.has(column.key) ? cards.length : columnLimit
          const dragItem = dragKey ? byKey.get(dragKey) : null
          const pickedItem = picked ? byKey.get(picked.key) : null
          const isTarget = (dragItem && overColumn === column.key && allowed(dragItem, column.key))
            || (pickedItem && picked.target === column.key && picked.target !== picked.from)
          return (
            <section
              key={column.key}
              ref={(node) => {
                if (node) columnRefs.current.set(column.key, node)
                else columnRefs.current.delete(column.key)
              }}
              aria-label={`${column.label}, ${count} ${count === 1 ? "card" : "cards"}`}
              className={cn(
                "flex max-h-[calc(100dvh-220px)] min-h-[160px] w-[85vw] shrink-0 snap-start flex-col rounded-card border bg-surface-2 transition-colors sm:w-[var(--board-col)]",
                isTarget ? "border-brand bg-brand-soft ring-2 ring-[color:var(--brand-soft-border)]" : "border-line"
              )}
              style={{ "--board-col": `${columnWidth}px` }}
              data-testid={column.testId || `${testId}-column-${column.key}`}
              data-drop-target={isTarget ? "true" : undefined}
              onDragOver={(event) => {
                if (!dragItem || !allowed(dragItem, column.key)) return
                event.preventDefault()
                if (overColumn !== column.key) setOverColumn(column.key)
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setOverColumn((current) => (current === column.key ? null : current))
              }}
              onDrop={(event) => onDrop(event, column.key)}
            >
              <header className="flex items-start gap-2 px-3 pb-2 pt-3">
                {column.tone ? <span className={cn("ui-tone-dot mt-[7px]", toneClass(column.tone))} aria-hidden="true" /> : null}
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-[13px] font-bold text-ink">{column.label}</h3>
                  {column.hint ? <p className="truncate text-[11.5px] text-ink-3">{column.hint}</p> : null}
                </div>
                <span className="rounded-full bg-surface px-2 py-0.5 text-[11.5px] font-bold tabular-nums text-ink-2 shadow-card">
                  {formatNumber(count, { maxDecimals: 0 })}
                </span>
              </header>
              <ul className="flex min-h-[48px] flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2" role="list">
                {cards.slice(0, limit).map((item) => {
                  const key = String(getKey(item))
                  const busy = busyKeys.has(key)
                  const isPicked = picked?.key === key
                  return (
                    <li
                      key={key}
                      ref={(node) => {
                        if (node) cardRefs.current.set(key, node)
                        else cardRefs.current.delete(key)
                      }}
                      tabIndex={0}
                      aria-describedby={movable ? instructionsId : undefined}
                      aria-roledescription={movable ? "movable card" : "card"}
                      aria-busy={busy || undefined}
                      draggable={movable && !busy}
                      onDragStart={(event) => {
                        setDragKey(key)
                        event.dataTransfer.effectAllowed = "move"
                        event.dataTransfer.setData("text/plain", key)
                      }}
                      onDragEnd={() => {
                        setDragKey(null)
                        setOverColumn(null)
                      }}
                      onKeyDown={(event) => onCardKeyDown(event, item)}
                      onBlur={(event) => onCardBlur(event, item)}
                      className={cn(
                        "group relative rounded-[12px] border border-line bg-surface p-3 text-[13px] shadow-card outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring",
                        movable && "cursor-grab active:cursor-grabbing",
                        dragKey === key && "opacity-50",
                        isPicked && "ring-2 ring-brand shadow-raised",
                        busy && "pointer-events-none opacity-60"
                      )}
                      data-testid={`${testId}-card-${key}`}
                      data-picked={isPicked ? "true" : undefined}
                    >
                      {renderCard(item, { busy, picked: isPicked })}
                      {isPicked && picked.target !== picked.from ? (
                        <span
                          className="mt-2 inline-flex items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[11.5px] font-bold text-brand-fg"
                          aria-hidden="true"
                          data-testid={`${testId}-target-hint`}
                        >
                          <MoveRight className="h-3 w-3" />{columnLabel(picked.target)}
                        </span>
                      ) : null}
                      {movable ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-lg text-ink-3 opacity-100 hover:bg-surface-hover hover:text-ink focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                              aria-label={`Move “${label(item)}” to…`}
                              data-testid={`${testId}-move-${key}`}
                              onKeyDown={(event) => event.stopPropagation()}
                            >
                              <MoveRight className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[200px]">
                            <DropdownMenuLabel className="text-[12px] text-ink-3">Move to</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {columns.map((target) => (
                              <DropdownMenuItem
                                key={target.key}
                                disabled={!allowed(item, target.key)}
                                onSelect={() => move(item, target.key)}
                                data-testid={`${testId}-move-${key}-to-${target.key}`}
                              >
                                {target.tone ? <span className={cn("ui-tone-dot", toneClass(target.tone))} aria-hidden="true" /> : null}
                                {target.label}
                                {target.key === getColumn(item) ? <span className="ml-auto text-[11.5px] text-ink-3">current</span> : null}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </li>
                  )
                })}
                {!cards.length ? (
                  <li className="grid flex-1 place-items-center rounded-[12px] border border-dashed border-[color:var(--line-dashed)] px-3 py-5 text-center text-[12.5px] text-ink-3">
                    {isTarget ? `Drop here to move to ${column.label}` : emptyText}
                  </li>
                ) : null}
              </ul>
              {cards.length > limit ? (
                <button
                  type="button"
                  className="mx-2 mb-2 rounded-lg py-1.5 text-[12.5px] font-semibold text-brand-ink hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setExpanded((current) => new Set(current).add(column.key))}
                >
                  Show {formatNumber(cards.length - limit, { maxDecimals: 0 })} more
                </button>
              ) : null}
              {column.footer ? <div className="border-t border-line px-3 py-2">{column.footer}</div> : null}
            </section>
          )
        })}
      </div>
    </div>
  )
}

export { Board }
